import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Combobox from './Combobox';
import { Field } from './Modal';
import { useConfirm } from './ConfirmDialog';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';

/**
 * <RelatedCompanies /> — the property's "Related companies" section.
 *
 * Replaces the fixed five-field version. Harvey wanted the section extensible:
 * add your own company TYPES (a lift maintainer, a window-cleaning contractor)
 * and add COMPANIES inline. Both are company-wide now — the directory lives on
 * the contractor record via /api/contractor/related-companies rather than in one
 * browser's localStorage, so what Harvey adds, Joe sees.
 *
 * Each type is a type-to-search picker over the shared directory, with "Add" for
 * a name that isn't in it yet.
 */
const FREE_TEXT = { key: 'otherContractors', label: 'Other contractors on site' };

const RelatedCompanies = ({ values, onSave, note }) => {
  const [types, setTypes] = useState([]);
  const [companies, setCompanies] = useState({});
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(values || {});
  const [saving, setSaving] = useState(false);
  const [newType, setNewType] = useState('');
  const [addingType, setAddingType] = useState(false);
  const [dirError, setDirError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const { confirm } = useConfirm();

  const load = useCallback(async () => {
    try {
      const d = await apiFetch('/api/contractor/related-companies');
      setTypes(Array.isArray(d?.types) ? d.types : []);
      setCompanies(d?.companies && typeof d.companies === 'object' ? d.companies : {});
      setDirError(null);
    } catch (e) { setDirError(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => { if (!editing) setDraft(values || {}); }, [values, editing]);

  const merged = useMemo(() => values || {}, [values]);
  // A type is worth showing read-only if it has a value; plus the free-text row.
  const filled = useMemo(
    () => types.filter((t) => merged[t.key]).map((t) => ({ ...t, value: merged[t.key] })),
    [types, merged],
  );
  const hasAny = filled.length > 0 || !!merged[FREE_TEXT.key];

  /** Remember a company name against a type, for everyone. */
  const rememberCompany = async (key, name) => {
    setCompanies((prev) => ({ ...prev, [key]: [...new Set([...(prev[key] || []), name])] }));
    try { await apiFetch('/api/contractor/related-companies', { method: 'POST', body: JSON.stringify({ key, name }) }); }
    catch (e) { toast.error(`“${name}” wasn't added to your shared company directory (${apiErrorMessage(e)}). It will still be saved on this property.`); }
  };

  const addType = async () => {
    const label = newType.trim();
    if (!label) return;
    setAddingType(true);
    try {
      const d = await apiFetch('/api/contractor/related-companies/types', { method: 'POST', body: JSON.stringify({ label }) });
      setTypes(Array.isArray(d?.types) ? d.types : types);
      setNewType('');
      toast.success(`“${label}” added — it's now available on every property`);
    } catch (err) {
      toast.error(`Couldn't add that company type: ${apiErrorMessage(err)}`);
    } finally { setAddingType(false); }
  };

  const removeType = async (t) => {
    const ok = await confirm({
      title: `Remove the “${t.label}” company type?`,
      body: 'It disappears from every property for your whole team. Names already saved on properties are kept.',
      confirmLabel: 'Remove type', tone: 'danger',
    });
    if (!ok) return;
    try {
      const d = await apiFetch(`/api/contractor/related-companies/types/${encodeURIComponent(t.key)}`, { method: 'DELETE' });
      setTypes(Array.isArray(d?.types) ? d.types : types.filter((x) => x.key !== t.key));
      toast.success(`“${t.label}” removed`);
    } catch (err) { toast.error(`Couldn't remove that type: ${apiErrorMessage(err)}`); }
  };

  const dirty = editing && JSON.stringify(draft || {}) !== JSON.stringify(values || {});
  const cancel = async () => {
    if (dirty && !(await confirm({ title: 'Discard changes?', body: "Your edits to related companies haven't been saved.", confirmLabel: 'Discard changes', tone: 'danger' }))) return;
    setEditing(false); setDraft(values || {}); setSaveError(null);
  };
  // onSave must throw on failure — the section then stays open with the error shown.
  const save = async () => {
    setSaving(true); setSaveError(null);
    try { await onSave(draft); setEditing(false); }
    catch (e) { setSaveError(apiErrorMessage(e, "Couldn't save — your changes are still here, try again")); }
    finally { setSaving(false); }
  };

  return (
    <section className="cw-card cw-card-pad" aria-label="Related companies">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: editing ? 12 : 8, gap: 10, flexWrap: 'wrap' }}>
        <h3 className="cw-h3" style={{ margin: 0 }}>Related companies</h3>
        {!editing ? (
          <button type="button" className="cw-btn cw-btn-secondary" onClick={() => { setDraft(values || {}); setSaveError(null); setEditing(true); }}>
            <i className={`fa-solid ${hasAny ? 'fa-pen' : 'fa-plus'}`} aria-hidden="true" /> {hasAny ? 'Edit' : 'Fill in'}
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="cw-btn cw-btn-secondary" onClick={cancel} disabled={saving}>Cancel</button>
            <button type="button" className="cw-btn cw-btn-primary" onClick={save} disabled={saving} aria-busy={saving || undefined}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        )}
      </div>

      {editing ? (
        <>
          <p className="cw-meta" style={{ margin: '0 0 12px' }}>
            Type to search your company directory, or add a name that isn't there yet. {note || 'Saved for this property; only your company sees it.'}
          </p>
          {saveError && <div className="cw-error-state compact" role="alert" style={{ marginBottom: 12 }}><div className="cw-error-state-text"><p className="cw-error-state-title">Not saved</p><p className="cw-error-state-reason">{saveError}</p></div></div>}
          {dirError && (
            <div className="cw-error-state compact" role="alert" style={{ marginBottom: 12 }}>
              <div className="cw-error-state-text">
                <p className="cw-error-state-title">Couldn't load your company directory</p>
                <p className="cw-error-state-reason">{apiErrorMessage(dirError)} You can still type names in; company types and suggestions are missing until it loads.</p>
              </div>
              <div className="cw-error-state-actions"><button type="button" className="cw-btn cw-btn-secondary" onClick={load}>Retry</button></div>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))', gap: 12 }} className="cw-field-grid">
            {types.map((t) => (
              <div key={t.key}>
                <Field label={t.label}>
                  <Combobox
                    value={draft[t.key] || ''}
                    options={companies[t.key] || []}
                    placeholder={`Search ${t.label.toLowerCase()}…`}
                    onChange={(v) => setDraft((d) => ({ ...d, [t.key]: v }))}
                    onAddNew={(v) => rememberCompany(t.key, v)}
                  />
                </Field>
                {!t.builtIn && (
                  <button type="button" className="cw-btn-link danger" style={{ fontSize: 'var(--cs-text-xs)', marginTop: 4 }}
                    aria-label={`Remove the ${t.label} company type`} onClick={() => removeType(t)}>Remove type</button>
                )}
              </div>
            ))}
            <Field label={FREE_TEXT.label} style={{ gridColumn: '1 / -1' }}>
              <input value={draft[FREE_TEXT.key] || ''} placeholder="e.g. Window cleaning, lift maintenance"
                onChange={(e) => setDraft((d) => ({ ...d, [FREE_TEXT.key]: e.target.value }))} />
            </Field>
          </div>

          {/* Extensible: a new type here appears on every property from now on. */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--cw-border-light)' }}>
            <Field label="Add a company type" hint="Company types are shared across your team and appear on every property.">
              <input value={newType} placeholder="e.g. Lift maintenance contractor"
                onChange={(e) => setNewType(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addType(); } }} />
            </Field>
            <button type="button" className="cw-btn cw-btn-secondary" style={{ marginTop: 8 }} onClick={addType} disabled={!newType.trim() || addingType}>
              <i className="fa-solid fa-plus" aria-hidden="true" /> {addingType ? 'Adding…' : 'Add type'}
            </button>
          </div>
        </>
      ) : hasAny ? (
        <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))', gap: 14, margin: 0 }}>
          {filled.map((t) => (
            <div key={t.key}>
              <dt className="cw-label">{t.label}</dt>
              <dd style={{ margin: '2px 0 0', fontSize: 'var(--cs-text-base)', fontWeight: 600 }}>{t.value}</dd>
            </div>
          ))}
          {merged[FREE_TEXT.key] && (
            <div style={{ gridColumn: '1 / -1' }}>
              <dt className="cw-label">{FREE_TEXT.label}</dt>
              <dd style={{ margin: '2px 0 0', fontSize: 'var(--cs-text-base)', fontWeight: 600 }}>{merged[FREE_TEXT.key]}</dd>
            </div>
          )}
        </dl>
      ) : (
        <p className="cw-meta" style={{ margin: 0 }}>
          Not filled in yet — choose <strong>Fill in</strong>. {note || 'Saved for this property; only your company sees it.'}
        </p>
      )}
    </section>
  );
};

export default RelatedCompanies;
